import {useWindowDimensions} from 'react-native';
import {usePosPreference} from './posPreferences';

/**
 * Width (in dp) at/above which the POS sales screen switches from the phone
 * layout (separate Sale / Cart tabs) to the tablet split view (BigSaleScreen:
 * catalog + cart side by side). Same app-wide phone/tablet convention as
 * MGMT_TABLET_BREAKPOINT in helpers/managementLayout.js.
 * @type {number}
 */
export const POS_TABLET_BREAKPOINT = 768;

/**
 * Width (in dp) below which the device is a "small phone" for the POS catalog
 * grid: tiles get the compact size and the column count is capped at
 * {@link POS_PHONE_MAX_COLUMNS} so every tile stays a comfortable tap target.
 * @type {number}
 */
export const POS_PHONE_BREAKPOINT = 480;

/** Most catalog columns a small phone renders (a 4th column is ~80dp wide). @type {number} */
export const POS_PHONE_MAX_COLUMNS = 3;

/** Most catalog columns the Design settings screen lets a terminal pick. @type {number} */
export const POS_MAX_COLUMNS = 5;

/**
 * Catalog column count the POS starts with on a fresh install, per screen width.
 * Phones get 2 columns: the legacy 1-column phone default rendered tall
 * single-file tiles that showed barely three products per screen.
 *
 * @param {number} width Current window width in dp.
 * @returns {number} Default column count for category, product and event grids.
 */
export const defaultPosGridColumns = (width) => {
    if (width < POS_TABLET_BREAKPOINT) {
        return 2;
    }

    return 3;
};

/**
 * Effective catalog column count for the current window. The stored value is
 * the terminal's Design setting; it is only clamped for display, never
 * rewritten, so the same setting still applies when the window grows again
 * (tablet rotation, desktop/web resize).
 *
 * @param {*} stored Column count from SystemContext (may be missing/garbage).
 * @param {number} width Current window width in dp.
 * @param {number} [fallback=2] Used when `stored` is not a positive number.
 * @returns {number} Integer column count in 1..POS_MAX_COLUMNS (1..3 on small phones).
 */
export const resolvePosGridColumns = (stored, width, fallback = 2) => {
    const parsed = Math.floor(Number(stored));
    const requested = Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    const max = width < POS_PHONE_BREAKPOINT ? POS_PHONE_MAX_COLUMNS : POS_MAX_COLUMNS;

    return Math.min(Math.max(requested, 1), max);
};

/**
 * Max width of one catalog tile for a given column count. FlatList rows use
 * `flex: 1` tiles, so the cap only matters for the last, partially filled row
 * — without it a lone tile stretches across the whole row. (The old fixed
 * `maxWidth: '50%'` also squeezed a 1-column grid to half the screen.)
 *
 * @param {number} columns Column count (>= 1).
 * @returns {string} Percentage string usable as a `maxWidth` style value.
 */
export const posTileMaxWidth = (columns) => {
    const safeColumns = Math.max(1, Math.floor(Number(columns)) || 1);

    return `${100 / safeColumns}%`;
};

/**
 * Whether a stored column count should be bumped by the one-time phone
 * migration: only the exact legacy phone default ('1') on a phone is touched,
 * so any column count a user deliberately picked is left alone.
 *
 * @param {?string} stored Raw AsyncStorage value.
 * @param {number} width Current window width in dp.
 * @returns {boolean} True when the value is the legacy phone default.
 */
export const isLegacyPhoneColumnDefault = (stored, width) => width < POS_TABLET_BREAKPOINT && stored === '1';

/** Height (dp, excluding the safe-area inset) of the phone POS bottom bar — a comfortable thumb target. @type {number} */
export const POS_TAB_BAR_HEIGHT = 66;

/** Diameter (dp) of the order-type button seated in the middle of the phone POS bar. @type {number} */
export const POS_FAB_SIZE = 56;

/** Width (dp) of the bar slot reserved for the order-type button. @type {number} */
export const POS_FAB_SLOT_WIDTH = 76;

/** Minimum touch target (dp) for primary POS actions on phones (Material 48 / HIG 44, rounded up). @type {number} */
export const POS_MIN_TOUCH = 52;

/**
 * Splits the tab routes around the centre slot of the phone POS bar so the
 * order-type button sits between two equal halves instead of covering a tab.
 * The centre slot is only used for an even route count; with an odd count the
 * routes stay in one row and the button has to float elsewhere.
 *
 * @param {Array} routes Navigation state routes, in tab order.
 * @returns {{left: Array, right: Array, hasCenterSlot: boolean}}
 */
export const splitPosTabRoutes = (routes) => {
    const list = Array.isArray(routes) ? routes : [];

    if (list.length < 2 || list.length % 2 !== 0) {
        return {left: list, right: [], hasCenterSlot: false};
    }

    const half = list.length / 2;

    return {left: list.slice(0, half), right: list.slice(half), hasCenterSlot: true};
};

/**
 * Whether the phone-first POS UI (custom tab bar, POS Hub, swipe cards, bottom
 * sheets) applies: only below the tablet breakpoint AND when the terminal did not
 * opt back into the "Classic" layout in Settings.
 *
 * @param {number} width Current window width in dp.
 * @param {string} [layoutMode='auto'] 'auto' | 'classic' (posPreferences.posLayoutMode).
 * @returns {boolean}
 */
export const resolvePosPhoneUi = (width, layoutMode = 'auto') => width < POS_TABLET_BREAKPOINT && layoutMode !== 'classic';

/**
 * Whether the restaurant (POS) mode should open on the POS Hub instead of the quick
 * receipt screen: phone UI only, and not when the terminal turned the Hub landing off.
 *
 * @param {object} args
 * @param {number} args.width Current window width in dp (0 / unknown → false).
 * @param {string} [args.layoutMode='auto'] posPreferences.posLayoutMode.
 * @param {boolean} [args.startOnHub=true] posPreferences.posStartOnHub.
 * @returns {boolean}
 */
export const shouldLandOnPosHub = ({width, layoutMode = 'auto', startOnHub = true}) => (
    typeof width === 'number' && width > 0 && resolvePosPhoneUi(width, layoutMode) && startOnHub !== false
);

/**
 * Live, orientation-aware layout hint for the POS sales screens (SaleScreen,
 * CartScreen, BigSaleScreen). Reads {@link useWindowDimensions}, so it follows
 * rotation, split view and desktop/web window resizes — unlike a module-level
 * `Dimensions.get('window')` snapshot.
 *
 * @returns {{width: number, height: number, isPhone: boolean, isCompact: boolean, isPhoneUi: boolean}}
 *   `isPhone`: below the tablet split-view breakpoint (Sale / Cart are separate tabs);
 *   `isCompact`: small phone, use the compact catalog tiles;
 *   `isPhoneUi`: `isPhone` and not on the "Classic" layout — gate every phone-first
 *   POS component (tab bar, Hub, swipe cards, sheets) on THIS, not on `isPhone`.
 */
export const usePosLayout = () => {
    const {width, height} = useWindowDimensions();
    const layoutMode = usePosPreference('posLayoutMode');

    return {
        width,
        height,
        isPhone: width < POS_TABLET_BREAKPOINT,
        isCompact: width < POS_PHONE_BREAKPOINT,
        isPhoneUi: resolvePosPhoneUi(width, layoutMode),
    };
};
